/**
 * "Alerts" (help `cloud.alerts`; docs/specs/cloud/alerts.md; Diurnal parity C1: 03-038, 06-056, 02-017). Each staff
 * member's own page:
 * - first (R122, MB 27 Oct 2026: "so staff are reminded to acknowledge alerts"), the organisation's stored alert
 *   history (`AlertHistory.tsx`), the alerts not yet acknowledged at its top: acknowledge, snooze, resolve and reopen
 *   for field crew, managers, data processors and tenant admins (parity C3, 02-015). It shows while the settings
 *   below still load;
 * - push on this device (the service worker `/teslyn-sw.js`), and the devices that receive their push alerts: their
 *   browsers and their phones signed in to Teslyn Mobile, each with a name of the person's own choosing (R158) that
 *   neither a browser subscribing again nor the phone app opening overwrites;
 * - what they receive, per kind: push and email each "Default", "Always" or "Never" (the defaults are the domain's,
 *   decision 53), and a test push per kind, as the Diurnal tool's five test buttons (06-056), and a test email.
 * Clients never see this page (SOS and lost tracking are staff only, Q37).
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  NOTICE_GROUP_LABEL,
  NOTICE_KINDS,
  DEVICE_NAME_MAX,
  deviceNameProblem,
  deviceShownName,
  noticeDefaultShort,
  noticeDefaultText,
  noticeKind,
  type NoticeChannel,
  type NoticeGroupId,
  type NoticeKind,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { AlertHistory } from './AlertHistory.tsx';
import {
  loadChoices,
  loadMyDevices,
  loadMyPhones,
  loadNames,
  loadPublicKey,
  removeDevice,
  renameDevice,
  saveChoice,
  saveDevice,
  sendTestNotice,
  type Choice,
  type MyDevice,
  type MyPhone,
} from './api.ts';
import {
  browserPushEnv,
  currentPush,
  deviceLabel,
  pushSupport,
  turnOffPush,
  turnOnPush,
  type PushEnv,
} from './push.ts';

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const when = (iso: string): string =>
  new Date(iso).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

type Setting = 'default' | 'on' | 'off';
const toSetting = (v: boolean | null | undefined): Setting =>
  v === true ? 'on' : v === false ? 'off' : 'default';
const fromSetting = (s: Setting): boolean | null => (s === 'on' ? true : s === 'off' ? false : null);

export interface AlertsScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly userId: string;
  readonly roles: readonly string[];
  /** The alert a notice or email opened (`?page=alerts&alert=<id>`). */
  readonly openAlertId?: string | null;
  /** The browser's push objects (fakes in tests). */
  readonly pushEnv?: PushEnv;
}

export function AlertsScreen({
  sb,
  tenantId,
  userId,
  roles,
  openAlertId = null,
  pushEnv,
}: AlertsScreenProps): ReactElement {
  const env = useMemo(() => pushEnv ?? browserPushEnv(), [pushEnv]);
  const [publicKey, setPublicKey] = useState<string | null | undefined>(undefined);
  const [devices, setDevices] = useState<MyDevice[] | null>(null);
  const [phones, setPhones] = useState<MyPhone[]>([]);
  // the device being named: its kind and id, and the name typed so far
  const [naming, setNaming] = useState<{
    readonly kind: 'browser' | 'phone';
    readonly id: string;
    readonly text: string;
  } | null>(null);
  const [here, setHere] = useState<string | null>(null);
  const [choices, setChoices] = useState<Map<string, Choice> | null>(null);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const support = pushSupport(env);

  const reload = useCallback(() => {
    setError(null);
    Promise.all([
      loadPublicKey(sb),
      loadMyDevices(sb, userId),
      loadChoices(sb, tenantId, userId),
      loadNames(sb, tenantId).catch(() => new Map<string, string>()),
      currentPush(env).catch(() => null),
      // no phone signed in, or none readable: no phones listed, and the rest of the page still works
      loadMyPhones(sb, userId).catch(() => [] as MyPhone[]),
    ])
      .then(([key, devs, ch, nm, cur, ph]) => {
        setPublicKey(key);
        setDevices(devs);
        setPhones(ph);
        setChoices(new Map(ch.map((c) => [c.kind, c])));
        setNames(nm);
        setHere(cur && devs.some((d) => d.endpoint === cur.endpoint) ? cur.endpoint : null);
      })
      .catch((e: unknown) => setError(message(e)));
  }, [sb, tenantId, userId, env]);
  useEffect(reload, [reload]);

  const run = (what: string, job: () => Promise<string | void>): void => {
    setBusy(what);
    setNotice(null);
    job()
      .then((text) => text && setNotice({ ok: true, text }))
      .catch((e: unknown) => setNotice({ ok: false, text: message(e) }))
      .finally(() => setBusy(null));
  };

  const turnOn = (): void =>
    run('push', async () => {
      if (!publicKey) throw new Error('Push alerts are not set up for Teslyn Cloud yet. Ask Braynewave.');
      const p = await turnOnPush(env, publicKey);
      await saveDevice(sb, p, deviceLabel(env.userAgent), window.location.origin);
      reload();
      return 'Push alerts are on for this device. Send yourself a test below.';
    });

  const turnOff = (): void =>
    run('push', async () => {
      const endpoint = await turnOffPush(env);
      if (endpoint) await removeDevice(sb, { endpoint });
      reload();
      return 'Push alerts are off for this device.';
    });

  const remove = (d: MyDevice): void =>
    run(`remove:${d.id}`, async () => {
      await removeDevice(sb, { id: d.id });
      if (d.endpoint === here) await turnOffPush(env).catch(() => null);
      reload();
      return `${deviceShownName({ kind: 'browser', name: d.device_name, label: d.device_label })} no longer receives push alerts.`;
    });

  const saveName = (): void => {
    if (!naming) return;
    const problem = deviceNameProblem(naming.text);
    if (problem) {
      setNotice({ ok: false, text: problem });
      return;
    }
    const { kind, id, text } = naming;
    run(`name:${id}`, async () => {
      const kept = await renameDevice(sb, kind, id, text);
      setNaming(null);
      reload();
      return kept ? `Named “${kept}”.` : 'Name cleared: the device shows its own label again.';
    });
  };

  /** A device's name, or the box to type a new one in, with Save name and Cancel. */
  const nameOf = (kind: 'browser' | 'phone', id: string, shown: string, isHere: boolean): ReactElement =>
    naming && naming.id === id ? (
      <span className="row row--wrap">
        <input
          type="text"
          className="input"
          spellCheck
          aria-label={`Name for ${shown}`}
          maxLength={DEVICE_NAME_MAX}
          placeholder={kind === 'phone' ? 'Work phone' : 'Office laptop'}
          value={naming.text}
          onChange={(e) => setNaming({ ...naming, text: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveName();
            if (e.key === 'Escape') setNaming(null);
          }}
        />
        <Button variant="primary" busy={busy === `name:${id}`} onClick={saveName}>
          Save name
        </Button>
        <Button variant="quiet" onClick={() => setNaming(null)}>
          Cancel
        </Button>
      </span>
    ) : (
      <strong>
        {shown}
        {isHere ? ' (this device)' : ''}
      </strong>
    );

  const choose = (kind: NoticeKind, channel: NoticeChannel, s: Setting): void => {
    const was = choices?.get(kind.id) ?? { kind: kind.id, push: null, email: null };
    const next: Choice = { ...was, [channel]: fromSetting(s) };
    setChoices((m) => new Map(m ?? []).set(kind.id, next));
    run(`choice:${kind.id}`, async () => {
      await saveChoice(sb, tenantId, userId, next);
      return `${kind.label}: saved.`;
    });
  };

  const test = (kind: NoticeKind, channel: NoticeChannel): void =>
    run(`test:${kind.id}:${channel}`, () => sendTestNotice(sb, tenantId, kind.id, channel));

  const loaded = publicKey !== undefined && devices && choices;
  const groups = [...new Set(NOTICE_KINDS.map((k) => k.group))] as NoticeGroupId[];

  return (
    <section className="page">
      <PageHeader
        title="Alerts"
        subtitle="SOS, lost tracking and other alerts by push on your phone or laptop, and by email. Acknowledge them here; below, choose what you receive."
      />
      <AlertHistory
        sb={sb}
        tenantId={tenantId}
        userId={userId}
        roles={roles}
        names={names}
        openAlertId={openAlertId}
      />
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!error && !loaded && <Loading what="your alert settings" />}
      {loaded && (
        <>
          <div className="panel stack">
            <h2 className="heading">Push on this device</h2>
            {support === 'unsupported' ? (
              <p className="body">
                This browser cannot receive push alerts. On an iPhone or iPad, add Teslyn Cloud to the home
                screen (Share, Add to Home Screen) and open it from there; elsewhere use Chrome, Edge, Firefox
                or Safari.
              </p>
            ) : support === 'blocked' ? (
              <p className="body">
                Notifications are blocked for this site. Allow them in the browser’s site settings, then turn
                push on here.
              </p>
            ) : publicKey === null ? (
              <p className="body">Push alerts are not set up for Teslyn Cloud yet. Ask Braynewave.</p>
            ) : here ? (
              <p className="body">On: this device receives your push alerts.</p>
            ) : (
              <p className="body">Off: this device does not receive push alerts.</p>
            )}
            {support === 'ready' && publicKey && (
              <div className="row">
                {here ? (
                  <Button onClick={turnOff} busy={busy === 'push'}>
                    {busy === 'push' ? 'Turning off…' : 'Turn off push on this device'}
                  </Button>
                ) : (
                  <Button variant="primary" onClick={turnOn} busy={busy === 'push'}>
                    {busy === 'push' ? 'Turning on…' : 'Turn on push on this device'}
                  </Button>
                )}
              </div>
            )}
            <h3 className="label muted">Your devices</h3>
            {devices.length === 0 && phones.length === 0 ? (
              <p className="body muted">No device receives your push alerts yet.</p>
            ) : (
              <ul className="stack">
                {devices.map((d) => {
                  const shown = deviceShownName({
                    kind: 'browser',
                    name: d.device_name,
                    label: d.device_label,
                  });
                  return (
                    <li key={d.id} className="row row--wrap">
                      <span className="body">
                        {nameOf('browser', d.id, shown, d.endpoint === here)}{' '}
                        <span className="caption muted">
                          · Browser{d.device_name && d.device_label ? `, ${d.device_label}` : ''} · added{' '}
                          {when(d.created_at)}
                          {d.last_ok_at ? ` · last alert ${when(d.last_ok_at)}` : ''}
                          {d.failures > 0 ? ` · ${d.failures} failed` : ''}
                        </span>
                      </span>
                      {naming?.id !== d.id && (
                        <span className="row">
                          <Button
                            variant="quiet"
                            aria-label={`Rename ${shown}`}
                            onClick={() =>
                              setNaming({ kind: 'browser', id: d.id, text: d.device_name ?? '' })
                            }
                          >
                            Rename
                          </Button>
                          <Button
                            variant="quiet"
                            aria-label={`Remove ${shown}`}
                            busy={busy === `remove:${d.id}`}
                            onClick={() => remove(d)}
                          >
                            Remove
                          </Button>
                        </span>
                      )}
                    </li>
                  );
                })}
                {phones.map((p) => {
                  const shown = deviceShownName({ kind: 'phone', name: p.device_name, label: p.label });
                  const push = !p.push_provider
                    ? 'notifications not allowed on the phone'
                    : p.push_expired_at
                      ? 'push address expired: open Teslyn Mobile on it'
                      : 'receives push alerts';
                  return (
                    <li key={p.id} className="row row--wrap">
                      <span className="body">
                        {nameOf('phone', p.id, shown, false)}{' '}
                        <span className="caption muted">
                          · Phone, Teslyn Mobile{p.app_version ? ` ${p.app_version}` : ''}
                          {p.device_name && p.label ? `, ${p.label}` : ''} · {push} · last opened{' '}
                          {when(p.last_seen_at)}
                        </span>
                      </span>
                      {naming?.id !== p.id && (
                        <Button
                          variant="quiet"
                          aria-label={`Rename ${shown}`}
                          onClick={() => setNaming({ kind: 'phone', id: p.id, text: p.device_name ?? '' })}
                        >
                          Rename
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {phones.length > 0 && (
              <p className="caption muted">
                A phone stops receiving your alerts when you sign out of Teslyn Mobile on it.
              </p>
            )}
          </div>

          <div className="panel stack">
            <h2 className="heading">What you receive</h2>
            <p className="caption muted">
              “Default” follows your place on a project’s crew, by its dates: SOS and lost tracking go to the
              operators and managers on the crew of the aircraft’s project, base station alerts to its
              operators. Once your crew row has ended you are on break, and “Default” sends you nothing, SOS
              included. When nobody on a crew would receive an alert, the organisation’s managers do. “Always”
              sends you every alert of that kind in the organisation, on break too.
            </p>
            {groups.map((g) => (
              <div key={g} className="stack">
                <h3 className="label muted">{NOTICE_GROUP_LABEL[g]}</h3>
                {/* The name and its description take the row's width; each choice sizes to its column (the
                    full meaning of "Default" is the choice's title). Under 900 px each alert is a card: the name,
                    the description, then Push, Email and Test one under another (styles.css .alerts-table). */}
                <table className="table alerts-table" aria-label={NOTICE_GROUP_LABEL[g]}>
                  <colgroup>
                    <col className="alerts-table__col-alert" />
                    <col className="alerts-table__col-choice" />
                    <col className="alerts-table__col-choice" />
                    <col className="alerts-table__col-test" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="label">Alert</th>
                      <th className="label">Push</th>
                      <th className="label">Email</th>
                      <th className="label">
                        <span className="sr-only">Test</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {NOTICE_KINDS.filter((k) => k.group === g).map((k) => {
                      const c = choices.get(k.id);
                      return (
                        <tr key={k.id}>
                          <td className="alerts-table__alert">
                            <span className="body">{k.label}</span>
                            <br />
                            <span className="caption muted">
                              {k.description}
                              {k.arrives ? ` ${k.arrives}` : ''}
                            </span>
                          </td>
                          {(['push', 'email'] as const).map((ch) => (
                            <td key={ch} className="alerts-table__choice">
                              <span className="label muted alerts-table__head" aria-hidden="true">
                                {ch === 'push' ? 'Push' : 'Email'}
                              </span>
                              <select
                                className="input"
                                aria-label={`${k.label} by ${ch}`}
                                title={`Default: ${noticeDefaultText(k, ch)}`}
                                value={toSetting(c?.[ch])}
                                onChange={(e) => choose(k, ch, e.target.value as Setting)}
                              >
                                <option value="default">Default ({noticeDefaultShort(k, ch)})</option>
                                <option value="on">Always</option>
                                <option value="off">Never</option>
                              </select>
                            </td>
                          ))}
                          <td className="table__actions alerts-table__test">
                            <Button
                              variant="quiet"
                              aria-label={`Test push: ${k.label}`}
                              disabled={
                                devices.length === 0 &&
                                !phones.some((p) => p.push_provider && !p.push_expired_at)
                              }
                              busy={busy === `test:${k.id}:push`}
                              onClick={() => test(k, 'push')}
                            >
                              {busy === `test:${k.id}:push` ? 'Sending…' : 'Test push'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
            <div className="row">
              <Button onClick={() => test(noticeKind('sos')!, 'email')} busy={busy === 'test:sos:email'}>
                {busy === 'test:sos:email' ? 'Sending…' : 'Send me a test email'}
              </Button>
              <span className="caption muted">
                From no-reply@teslyn.au to the email address you sign in with. At most 10 tests an hour.
              </span>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
